/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import * as API from '../utils/api';
import Types from '../constants/actionTypes';
import Store from '../store/store';
import { applicationError } from './notificationActions';
import { uploadAttachments } from './attachmentActions';
import { fetchCriteria } from './criteriaActions';
import { openModal } from './modalActions';
import {
  fetchAllWorkPackages,
  setSelectedSpace,
  removeSpace
} from './workPackageActions';
import modalTypes from '../constants/modalTypes';
import { updateSingleResult } from './commonDispatches';
import { getSelectedSpace, getTeam } from '../utils/reduxHelpers';

const dataFormatter = new Jsona();

const currentProductId = () => Store.getState().productReducer.product.id;
const cadenceId = () => Store.getState().cadenceReducer.cadence?.id;

const dispatchRemoveTeamAssociations = (dispatch, product, teamId) => {
  const { id: productId } = product;

  dispatch({
    type: Types.UPDATE_PRODUCT,
    product
  });

  dispatch({
    type: Types.REMOVE_PRODUCT_FROM_TEAM,
    teamId,
    productId
  });

  const selectedSpace = getSelectedSpace();

  if (selectedSpace.type === 'team' && selectedSpace.id === teamId) {
    dispatch({
      type: Types.DELETE_PRODUCT_FROM_TEAM_SPACE,
      productId
    });

    dispatch({
      type: Types.REMOVE_CURRENT_PRODUCT,
      productId
    });
  } else if (
    selectedSpace.type === 'product' &&
    selectedSpace.id === productId
  ) {
    const team = getTeam(teamId);
    dispatch({
      type: Types.DELETE_TEAM_FROM_PRODUCT_SPACE,
      teamId
    });

    dispatch({
      type: Types.REMOVE_CURRENT_TEAM,
      teamId
    });
  }
};

const dispatchAddTeamAssociations = (dispatch, product, teamId) => {
  dispatch({
    type: Types.UPDATE_PRODUCT,
    product
  });

  dispatch({
    type: Types.ADD_PRODUCT_TO_TEAM,
    product,
    teamId
  });

  const selectedSpace = getSelectedSpace();

  if (selectedSpace.type === 'team' && selectedSpace.id === teamId) {
    dispatch({
      type: Types.ADD_PRODUCT_TO_TEAM_SPACE,
      product
    });

    dispatch({
      type: Types.ADD_CURRENT_PRODUCT,
      product
    });
  } else if (
    selectedSpace.type === 'product' &&
    selectedSpace.id === product.id
  ) {
    const team = getTeam(teamId);
    dispatch({
      type: Types.ADD_CURRENT_TEAM,
      team
    });

    dispatch({
      type: Types.ADD_TEAM_TO_PRODUCT_SPACE,
      team
    });
  }
};

export const handleProductBroadcast = (product, type, teamId) => (dispatch) => {
  switch (type) {
    case 'update':
      return dispatch({ type: Types.UPDATE_PRODUCT, product });
    case 'create':
      return dispatch({ type: Types.ADD_PRODUCT, product });
    case 'delete':
      return dispatch({ type: Types.DELETE_PRODUCT, productId: product });
    case 'add_team':
      return dispatchAddTeamAssociations(dispatch, product, teamId);
    case 'remove_team':
      return dispatchRemoveTeamAssociations(dispatch, product, teamId);
    default:
  }
};

export const getProduct = (id) => (dispatch) => {
  dispatch({ type: Types.REQUEST_PRODUCT });
  return API.get(`products/${id}`)
    .then((response) => {
      dispatch({ type: Types.SET_PRODUCT, product: response.product.id });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const setProduct = (id) => (dispatch) =>
  dispatch({ type: Types.SET_PRODUCT, product: id });

export const getAllProducts = () => (dispatch) => {
  dispatch({ type: Types.REQUEST_PRODUCT });
  return API.get('products')
    .then((response) => {
      const products = dataFormatter.deserialize(response);
      dispatch({ type: Types.RECEIVE_PRODUCTS, products });
    })
    .catch((error) => {
      dispatch(applicationError(error));
      dispatch({ type: Types.REQUEST_PRODUCT_FAILED });
    });
};

export const createProduct = (data, attachments) => (dispatch) => {
  dispatch({ type: Types.SEND_PRODUCT });
  const {
    product: { teams, ...newProduct }
  } = data;
  return API.post('products', { product: newProduct })
    .then((response) => response.json())
    .then((response) => {
      const product = dataFormatter.deserialize(response);
      if (attachments.length > 0) {
        return dispatch(uploadAttachments(attachments, product.id)).then(() => {
          dispatch({ type: Types.ADD_PRODUCT, product });
          dispatch({ type: Types.CLOSE_MODAL, id: 'productCard' });
        });
      }
      dispatch(fetchCriteria(product.id, 'success'));
      dispatch({ type: Types.ADD_PRODUCT, product });
      if (!Store.getState().workPackageReducer.spaceSelected)
        dispatch(setSelectedSpace(product, 'product'));
      return dispatch({ type: Types.CLOSE_MODAL, id: 'productCard' });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const updateProduct = (id, data) => (dispatch) => {
  dispatch({ type: Types.SEND_PRODUCT });
  return API.put(`products/${id}`, data)
    .then((response) => response.json())
    .then((response) => {
      const product = dataFormatter.deserialize(response);
      dispatch({
        type: Types.UPDATE_PRODUCT,
        product
      });
    })
    .catch((error) => dispatch(applicationError(error)));
};

export const deleteProduct = (id) => (dispatch) => {
  const allWorkPackages = Store.getState().workPackageReducer.all;
  if (allWorkPackages.filter((wp) => wp.product_id === id).length > 0) {
    return dispatch(
      applicationError(
        'You must remove or reassign the work packages assigned to this product before deleting it'
      )
    );
  }
  dispatch({ type: Types.SEND_PRODUCT });
  return API.del(`products/${id}`)
    .then((response) => {
      if (response.ok) {
        dispatch({
          type: Types.DELETE_PRODUCT,
          productId: id
        });
      }
    })
    .then(() => {
      dispatch(removeSpace(id, 'product'));
    })
    .catch((error) => dispatch(applicationError(error)));
};

export function addResultToProduct(resultId, productId) {
  const payload = { result_id: resultId };
  return (dispatch) => {
    dispatch({ type: Types.SEND_PRODUCT });
    return API.put(
      `products/${productId || currentProductId()}/add-result`,
      payload
    )
      .then((response) => response.json())
      .then((response) => {
        const workPackage = response.work_package;
        updateSingleResult(dispatch, workPackage);
        dispatch({
          type: Types.ADD_WORK_PACKAGE_TO_PRODUCT,
          workPackage,
          productId
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeResultFromProduct(resultId, productId) {
  const payload = { result_id: resultId };
  return (dispatch) => {
    dispatch({ type: Types.SEND_PRODUCT });
    return API.put(
      `products/${productId || currentProductId()}/remove-result`,
      payload
    )
      .then((response) => response.json())
      .then((response) => {
        const workPackage = response.work_package;
        updateSingleResult(dispatch, workPackage);
        dispatch({
          type: Types.REMOVE_WORK_PACKAGE_FROM_PRODUCT,
          workPackage,
          productId,
          cadence: cadenceId()
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addTeamToProduct(teamId, productId) {
  const payload = { team_id: teamId };
  return (dispatch) => {
    dispatch({ type: Types.SEND_PRODUCT });
    return API.put(
      `products/${productId || currentProductId()}/add-team`,
      payload
    )
      .then((response) => response.json())
      .then((response) => {
        const product = dataFormatter.deserialize(response);
        dispatchAddTeamAssociations(dispatch, product, teamId);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

const removeTeamFromProductWithAssociatedResults = (
  teamId,
  productId,
  dispatch,
  refreshResults
) => {
  const payload = { team_id: teamId, remove_associations: true };
  dispatch({ type: Types.SEND_PRODUCT });
  return API.put(
    `products/${productId || currentProductId()}/remove-team`,
    payload
  )
    .then((response) => response.json())
    .then((response) => {
      const product = dataFormatter.deserialize(response);
      dispatchRemoveTeamAssociations(dispatch, product, teamId);
      if (refreshResults) {
        dispatch(fetchAllWorkPackages());
      }
    })
    .catch((error) => dispatch(applicationError(error)));
};

const renderTeamRemovalModal = (
  teamId,
  productId,
  dispatch,
  message = null,
  count = 0,
  refreshResults
) => {
  const defaultMessage = `This team has ${count} work packages associated with this product. If you proceed, they will be unassigned from this team. Proceed?`;

  const actions = {
    confirm: {
      name: 'Yes',
      function: () =>
        removeTeamFromProductWithAssociatedResults(
          teamId,
          productId,
          dispatch,
          refreshResults
        )
    }
  };

  dispatch(
    openModal(
      modalTypes.message,
      {
        message: message || defaultMessage,
        actions,
        closeButtonTitle: 'No'
      },
      'confirm'
    )
  );
};

export function removeTeamFromProduct(teamId, productId) {
  const payload = { team_id: teamId };
  const workPackages = Store.getState().workPackageReducer.all;
  const workPackageCount = workPackages.filter(
    (wp) => wp.team_id === teamId && wp.product_id === productId
  ).length;

  if (workPackageCount) {
    return (dispatch) => {
      renderTeamRemovalModal(
        teamId,
        productId,
        dispatch,
        null,
        workPackageCount,
        true
      );
    };
  }

  return (dispatch) => {
    dispatch({ type: Types.SEND_PRODUCT });
    return API.put(
      `products/${productId || currentProductId()}/remove-team`,
      payload
    )
      .then((response) => {
        if (response.status === 405) {
          renderTeamRemovalModal(
            teamId,
            productId,
            dispatch,
            response.message,
            0,
            false
          );
          return null;
        }
        return response.json();
      })
      .then((response) => {
        if (!response) return;
        const product = dataFormatter.deserialize(response);
        dispatchRemoveTeamAssociations(dispatch, product, teamId);
      })
      .catch((error) => {
        dispatch(applicationError(error));
      });
  };
}

export function updateProductStatus(productId, state) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_PRODUCT });
    return API.put(
      `products/${productId || currentProductId()}/change-status`,
      { state }
    )
      .then((response) => response.json())
      .then((response) => {
        const product = dataFormatter.deserialize(response);
        dispatch({
          type: Types.UPDATE_PRODUCT,
          product
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}
